import { AgentLog, AgentPrompt, AgentTestInput, AgentTestResult, AgentRating } from '@/types/agent-types';
import { API_BASE_URL as CONFIG_API_BASE_URL } from '@/config/api';
import { authFetch } from '@/utils/auth-fetch';

// Use the base URL from config (without /api suffix since we explicitly include it in each endpoint)
const API_BASE_URL = CONFIG_API_BASE_URL;

// API URL constants for consistent endpoints
const API_ENDPOINTS = {
  AGENT_TEST: `${API_BASE_URL}/agents/test-run`,
  // Updated to use the correct prompts endpoint (plural)
  AGENT_PROMPTS: (agentType: string) => `${API_BASE_URL}/agents/${agentType}/prompts`,
  AGENT_BY_ID: (agentId: string) => `${API_BASE_URL}/agents/${agentId}`,
  // Agent logs endpoint - the server.js endpoint expects query param format
  AGENT_LOGS: (agentType: string) => `${API_BASE_URL}/agents/logs`,
  // Debug endpoints - for direct access in case of middleware issues
  DEBUG_LOGS: `${API_BASE_URL}/debug/logs`,
  DEBUG_AGENT_LOGS: (agentType: string) => `${API_BASE_URL}/debug/agent-logs/${agentType}`
};

// For debugging - log API requests
const debugMode = true; // Set to false in production
const debugLog = (message: string, data?: any) => {
  if (debugMode) {
    console.log(`[AgentAPI] ${message}`, data || '');
  }
};

/**
 * API service for agent-related operations with fixed endpoints
 */
export const agentApiFixed = {
  /**
   * Fetch all available agent types from the database
   */
  async getAgentTypes(): Promise<string[]> {
    debugLog('Fetching agent types from database with fallback');
    try {
      // Try to fetch from database API endpoint
      const response = await authFetch(`${API_BASE_URL}/agents/types`);

      if (response.ok) {
        const data = await response.json();
        debugLog('Successfully fetched agent types from database', data);

        if (Array.isArray(data) && data.length > 0) {
          return data;
        }
      }

      // Try to fetch from all agents and extract types
      const agentsResponse = await authFetch(`${API_BASE_URL}/agents`);

      if (agentsResponse.ok) {
        const agentsData = await agentsResponse.json();
        if (agentsData && agentsData.agents && Array.isArray(agentsData.agents)) {
          // Extract unique agent_type values from the agents collection
          const agentTypes = new Set<string>();

          agentsData.agents.forEach((agent: any) => {
            if (agent.agent_type) {
              agentTypes.add(agent.agent_type);
            }
          });

          if (agentTypes.size > 0) {
            const types = Array.from(agentTypes);
            debugLog('Extracted agent types from agents collection', types);
            return types;
          }
        }
      }

      // If all else fails, fall back to hardcoded values
      debugLog('Falling back to hardcoded agent types');
      return [
        'page-role', 
        'page-semantic-intent', 
        'customer-intent', 
        'question-suggestion', 
        'adjacent-topics',
        'auto-evaluator',
        'competitive-analysis',
        'intent-based-recommender',
        'keyword-extractor',
        'mock-page-chatbot',
        'narrative-presentation',
        'page-chatbot',
        'site-crawler',
        'style-suggestion',
        'voice-assistant',
        'voice-chatbot'
      ];
    } catch (error) {
      console.error('Error fetching agent types:', error);
      return [
        'page-role', 
        'page-semantic-intent', 
        'customer-intent', 
        'question-suggestion', 
        'adjacent-topics',
        'auto-evaluator',
        'competitive-analysis',
        'intent-based-recommender',
        'keyword-extractor',
        'mock-page-chatbot',
        'narrative-presentation',
        'page-chatbot',
        'site-crawler',
        'style-suggestion',
        'voice-assistant',
        'voice-chatbot'
      ];
    }
  },

  /**
   * Get the latest prompt for a specific agent type or ID
   */
  async getLatestPrompt(agentId: string): Promise<AgentPrompt> {
    debugLog(`Fetching latest prompt for ${agentId} from API`);

    try {
      // If this looks like a MongoDB ID, try to convert it to an agent type
      // MongoDB IDs are typically 24 hex characters
      if (/^[0-9a-f]{24}$/i.test(agentId)) {
        // Try to get the agent first to determine its type
        debugLog(`${agentId} appears to be a MongoDB ID, trying to fetch agent details`);
        try {
          // Use consistent API endpoint from our constants
          const agentResponse = await authFetch(API_ENDPOINTS.AGENT_BY_ID(agentId));
          if (agentResponse.ok) {
            const agentData = await agentResponse.json();
            if (agentData && agentData.agent && agentData.agent.agent_type) {
              agentId = agentData.agent.agent_type;
              debugLog(`Converted agent ID to type: ${agentId}`);
            }
          } else {
            debugLog(`Failed to fetch agent with ID ${agentId}, response status: ${agentResponse.status}`);
            // Try a fallback method by checking for standard agent type mappings
            const standardTypes = [
              'page-role', 'page-semantic-intent', 'customer-intent', 
              'adjacent-topics', 'question-suggestion', 'auto-evaluator'
            ];
            // Just use a default type since we can't determine the actual type
            agentId = 'page-role';
            debugLog(`Using default agent type: ${agentId}`);
          }
        } catch (error) {
          debugLog(`Error converting agent ID: ${error}`);
          // Continue with original ID if conversion fails
        }
      }

      // Now try to get the prompt with either the original ID or converted type
      // Use the correct prompts endpoint (from our tests)
      const response = await authFetch(API_ENDPOINTS.AGENT_PROMPTS(agentId));

      // If successful, return the most recent prompt from the array
      if (response.ok) {
        const promptsData = await response.json();
        debugLog(`Successfully retrieved prompts for ${agentId} from API`);
        
        // If the response includes prompts, return the most recent one
        if (promptsData && promptsData.prompts && promptsData.prompts.length > 0) {
          // Sort by created_at (newest first) and take the first one
          const sortedPrompts = [...promptsData.prompts].sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          
          debugLog(`Found ${promptsData.prompts.length} prompts, using most recent one`);
          return sortedPrompts[0];
        } else {
          debugLog(`No prompts found for ${agentId}`);
        }
      }

      debugLog(`Failed to retrieve prompts for ${agentId} from API, using fallback initialization`);
      
      // Determine if this is a known agent type to provide appropriate fallback
      let knownAgentType = agentId;
      // If it looks like a MongoDB ID and we failed to convert it earlier, use a default
      if (/^[0-9a-f]{24}$/i.test(agentId)) {
        debugLog(`Using default fallback for MongoDB ID ${agentId}`);
        knownAgentType = 'customer-intent'; // Default fallback for MongoDB IDs
      }

      // If the API fails, try initialization scripts as fallback
      if (agentId === 'page-role') {
        await authFetch(`${API_BASE_URL}/admin/init-page-role-agent`);
        const initResult = await authFetch(`${API_BASE_URL}/scripts/initialize-page-role-agent`);
        if (initResult.ok) {
          debugLog('Page role agent initialization successful');
        } else {
          debugLog('Page role agent initialization failed, using fallback prompt');
        }
      } else if (agentId === 'page-semantic-intent') {
        await authFetch(`${API_BASE_URL}/admin/init-page-semantic-intent-agent`);
        const initResult = await authFetch(`${API_BASE_URL}/scripts/initialize-page-semantic-intent-agent`);
        if (initResult.ok) {
          debugLog('Page semantic intent agent initialization successful');
        } else {
          debugLog('Page semantic intent agent initialization failed, using fallback prompt');
        }
      } else if (agentId === 'question-suggestion') {
        // Initialize question-suggestion agent if not already initialized
        const initResult = await authFetch(`${API_BASE_URL}/scripts/initialize-question-suggestion-agent`);
        if (initResult.ok) {
          debugLog('Question suggestion agent initialization successful');
          // Try to get the prompt again after initialization
          const retryResponse = await authFetch(`${API_BASE_URL}/agents/prompt/${agentId}`);
          if (retryResponse.ok) {
            return await retryResponse.json();
          }
        } else {
          debugLog('Question suggestion agent initialization failed, using fallback prompt');
        }
      } else if (agentId === 'keyword-extractor') {
        // Initialize keyword-extractor agent if not already initialized
        const initResult = await authFetch(`${API_BASE_URL}/scripts/initialize-keyword-extractor-agent`);
        if (initResult.ok) {
          debugLog('Keyword extractor agent initialization successful');
          // Try to get the prompt again after initialization
          const retryResponse = await authFetch(`${API_BASE_URL}/agents/prompt/${agentId}`);
          if (retryResponse.ok) {
            return await retryResponse.json();
          }
        } else {
          debugLog('Keyword extractor agent initialization failed, using fallback prompt');
        }
      } else if (agentId === 'style-suggestion') {
        // Initialize style-suggestion agent if not already initialized
        const initResult = await authFetch(`${API_BASE_URL}/scripts/initialize-style-suggestion-agent`);
        if (initResult.ok) {
          debugLog('Style suggestion agent initialization successful');
          // Try to get the prompt again after initialization
          const retryResponse = await authFetch(`${API_BASE_URL}/agents/prompt/${agentId}`);
          if (retryResponse.ok) {
            return await retryResponse.json();
          }
        } else {
          debugLog('Style suggestion agent initialization failed, using fallback prompt');
        }
      }

      // Return fallback prompt based on agent type
      let promptTemplate = '';

      if (agentId === 'page-role') {
        promptTemplate = `
You are an AI assistant that analyzes web page content to determine the most likely role(s) of the page.

Analyze the following page content and determine its role(s):

Page ID: {{pageId}}
Session ID: {{sessionId}}
Content: {{pageContent}}

Common page roles include:
- Product listing/catalog
- Product detail
- Homepage
- Blog post/article
- Contact page
- About page
- Checkout/cart
- Login/registration
- Form page
- Search results
- Category/navigation page
- Landing page
- Documentation page
- Profile page

For each role you identify:
1. Assign a confidence score (0.0-1.0)
2. Include brief evidence from the page content that supports this role

Respond in JSON format like this:
{
  "roles": [
    {
      "role": "product_listing",
      "confidence": 0.95,
      "evidence": "Page contains multiple products with images, prices, and add-to-cart buttons"
    },
    {
      "role": "search_results",
      "confidence": 0.40,
      "evidence": "Has filtering options that suggest search functionality"
    }
  ]
}

Be precise in your analysis and base your decisions solely on the provided content.
`;
      } else if (agentId === 'page-semantic-intent') {
        promptTemplate = `
You are an AI assistant that analyzes web page content to extract semantic categories, topics, and user intent signals.

Analyze the following page content:

Page ID: {{pageId}}
Session ID: {{sessionId}}
Content: {{pageContent}}

Your task is to:

1. Determine the primary semantic category of the page (e.g., e-commerce, blog, news, documentation)

2. Identify the key topics mentioned on the page, with a relevance score (0.0-1.0)

3. Detect user intent signals from the page, with confidence scores (0.0-1.0)
   Common intents include:
   - Purchase/Buy
   - Learn/Research
   - Compare Products
   - Find Information
   - Subscribe/Sign up
   - Contact/Support
   - Share/Social

4. Extract any additional metadata that might be useful (optional)

Respond in JSON format like this:
{
  "semanticCategory": "e-commerce",
  "topics": [
    { "name": "smartphones", "relevance": 0.95 },
    { "name": "mobile technology", "relevance": 0.75 },
    { "name": "electronics", "relevance": 0.60 }
  ],
  "intents": [
    {
      "intent": "purchase",
      "confidence": 0.90,
      "evidence": "Contains product listings with prices and 'Add to Cart' buttons"
    },
    {
      "intent": "compare",
      "confidence": 0.80,
      "evidence": "Multiple product options with specifications for comparison"
    }
  ],
  "metadata": {
    "contentLength": 1250,
    "languageHints": ["en"],
    "keyPhrases": ["premium smartphones", "free shipping"]
  }
}

Be precise in your analysis and base your decisions solely on the provided content.
`;
      } else if (agentId === 'customer-intent') {
        promptTemplate = `
You are an AI assistant specialized in analyzing customer intent from web page interactions.

Analyze the following customer interaction:

Page ID: {{pageId}}
Session ID: {{sessionId}}
Customer Message: {{customerMessage}}
Page Content: {{pageContent}}
{{#if imagePath}}
Customer screenshot available at: {{imagePath}}
{{/if}}

Your task is to:

1. Determine the primary customer intent (e.g., purchase, inquiry, complaint, browsing)
2. Assess sentiment (positive, negative, neutral) with score
3. Evaluate urgency level
4. Determine if this requires actionable follow-up
5. Suggest potential responses (optional)

Common customer intents include:
- Purchase/Buy intent
- Product information inquiry
- Price comparison
- Technical support/help
- Account management
- Complaint/negative feedback
- General browsing/research
- Request for assistance/guidance

Respond in JSON format:
{
  "intent": {
    "primaryIntent": "purchase",
    "confidence": 0.85,
    "secondaryIntents": [
      { "intent": "price_comparison", "confidence": 0.45 }
    ]
  },
  "sentiment": {
    "overall": "positive",
    "score": 0.75
  },
  "urgency": {
    "level": "medium",
    "score": 0.6
  },
  "actionable": true,
  "suggestedResponses": [
    "Would you like more information about our pricing options?",
    "Can I help you complete your purchase?"
  ],
  "analysisNotes": "Customer shows strong buying signals with price sensitivity."
}

Be precise in your analysis and base your decisions solely on the provided content.
`;
      } else if (agentId === 'question-suggestion') {
        promptTemplate = `
You are an AI assistant specialized in generating relevant and helpful questions about web page content.

Analyze the following page content:

Page ID: {{pageId}}
Session ID: {{sessionId}}
Page Content: {{pageContent}}

Your task is to:

1. Identify the key topics and concepts on the page
2. Understand what a customer might want to know more about
3. Generate THREE relevant, helpful questions that a customer browsing this page might want to ask

Focus on questions that would:
- Help customers make informed decisions
- Clarify information that might not be fully explained on the page
- Address common concerns or needs related to the content
- Guide customers toward valuable information that is related to what they're viewing

Avoid:
- Basic questions that are already clearly answered in the content
- Overly generic questions that don't relate specifically to the page content
- Questions that might lead to customer frustration
- Multiple questions asking about the same topic

Respond in JSON format:
{
  "suggestedQuestions": [
    "What financing options are available for this product?",
    "How does this compare to your other models in terms of features?",
    "Do you offer installation services for this product?"
  ],
  "context": "Product page for a high-end appliance with technical specifications and pricing information",
  "confidence": 0.85
}

Be precise in your suggestions and base your questions solely on the provided content.
`;
      } else if (agentId === 'adjacent-topics') {
        promptTemplate = `
You are an AI assistant specialized in identifying related topics that complement the main content of a web page.

Analyze the following page content:

Page ID: {{pageId}}
Session ID: {{sessionId}}
Page Content: {{pageContent}}

Your task is to:

1. Identify the primary topic or focus of the page
2. Determine 3-5 adjacent or related topics that would be valuable to the reader
3. For each adjacent topic, explain why it's relevant and how it complements the main content
4. Assign a relevance score (0.0-1.0) to each adjacent topic

Focus on identifying topics that:
- Extend or complement the user's current interest
- Provide additional context or depth to the main topic
- Represent natural next steps in a user's research or buying journey
- Fill potential knowledge gaps related to the main topic

Respond in JSON format:
{
  "primaryTopic": "Home coffee brewing equipment",
  "adjacentTopics": [
    {
      "topic": "Coffee bean selection guide",
      "relevance": 0.92,
      "rationale": "Users interested in coffee equipment would benefit from knowing which coffee beans work best with different brewing methods"
    },
    {
      "topic": "Water quality and coffee brewing",
      "relevance": 0.85,
      "rationale": "Water quality significantly affects coffee taste but is often overlooked by beginners"
    },
    {
      "topic": "Coffee grinder comparison",
      "relevance": 0.90,
      "rationale": "The coffee grinder is as important as the brewing equipment for quality results"
    }
  ],
  "confidence": 0.88
}

Be precise in your analysis and base your suggestions solely on the provided content.
`;
      } else if (agentId === 'style-suggestion') {
        promptTemplate = `
You are an AI assistant specialized in analyzing website styles and suggesting compatible design tokens for UI elements.

Analyze the following page content:

Page ID: {{pageId}}
Session ID: {{sessionId}}
Page Content: {{pageContent}}

Your task is to:

1. Analyze the HTML and CSS to understand the website's visual style and theme
2. Extract the main color scheme used on the page
3. Identify font families and sizes for main text elements
4. Determine common UI patterns (border radius, shadows, spacing, etc.)
5. Generate a set of design tokens that would create UI elements that blend seamlessly with the site's aesthetic

Consider these aspects of the site's design:
- Primary color scheme and accent colors
- Text colors and contrast ratios
- Background styles and patterns
- Typography choices (serif vs sans-serif, font weights, etc.)
- UI element styling (flat vs. shadowed, rounded vs. sharp corners)
- Overall aesthetic (minimalist, corporate, playful, etc.)
- Dark mode vs. light mode

Respond in JSON format:
{
  "colorScheme": {
    "primary": "#3B82F6",
    "background": "#F9FAFB",
    "text": "#111827"
  },
  "font": {
    "family": "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif",
    "size": "16px"
  },
  "borderRadius": "0.375rem",
  "shadow": "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  "additionalNotes": "Modern, clean design with light background and blue accent colors. UI elements have subtle shadows and rounded corners."
}

If you cannot confidently determine the style, use these neutral defaults:
- Primary color: #4F46E5 (indigo)
- Background: #FFFFFF (white) or #1F2937 (dark gray) if dark mode detected
- Text: #111827 (near black) or #F9FAFB (near white) if dark mode detected
- Font family: system-ui, -apple-system, sans-serif
- Border radius: 0.25rem (4px)
- Shadow: 0 1px 3px rgba(0, 0, 0, 0.12)

Be precise in your analysis and base your suggestions solely on the provided content.
`;
      } else {
        promptTemplate = 'No prompt template available for this agent type.';
      }

      const fallbackPrompt: AgentPrompt = {
        _id: 'mock-id',
        agent_type: agentId,
        prompt: promptTemplate,
        version: '1.0.0',
        created_at: new Date()
      };
      
      return fallbackPrompt;
    } catch (error) {
      console.error(`Error fetching prompt for ${agentId}:`, error);

      // For debugging purposes, return a more informative error message
      return {
        _id: 'mock-id',
        agent_type: agentId,
        prompt: `
You are an AI assistant that analyzes web page content to determine the most likely role(s) of the page.

Analyze the following page content and determine its role(s):

Page ID: {{pageId}}
Session ID: {{sessionId}}
Content: {{pageContent}}

Common page roles include:
- Product listing/catalog
- Product detail
- Homepage
- Blog post/article
- Contact page
- About page
- Checkout/cart
- Login/registration
- Form page
- Search results
- Category/navigation page
- Landing page
- Documentation page
- Profile page

For each role you identify:
1. Assign a confidence score (0.0-1.0)
2. Include brief evidence from the page content that supports this role

Respond in JSON format like this:
{
  "roles": [
    {
      "role": "product_listing",
      "confidence": 0.95,
      "evidence": "Page contains multiple products with images, prices, and add-to-cart buttons"
    },
    {
      "role": "search_results",
      "confidence": 0.40,
      "evidence": "Has filtering options that suggest search functionality"
    }
  ]
}

Be precise in your analysis and base your decisions solely on the provided content.
`,
        version: '1.0.0',
        created_at: new Date()
      };
    }
  },

  /**
   * Save a new version of a prompt for an agent type
   */
  async savePrompt(agentId: string, prompt: string): Promise<AgentPrompt> {
    debugLog(`Saving prompt for ${agentId}`);

    try {
      // Convert MongoDB ID to agent type if needed
      let agentTypeId = agentId;
      if (/^[0-9a-f]{24}$/i.test(agentId)) {
        try {
          const agentResponse = await authFetch(API_ENDPOINTS.AGENT_BY_ID(agentId));
          if (agentResponse.ok) {
            const agentData = await agentResponse.json();
            if (agentData && agentData.agent && agentData.agent.agent_type) {
              agentTypeId = agentData.agent.agent_type;
              debugLog(`Converted agent ID to type: ${agentTypeId}`);
            }
          } else {
            debugLog(`Failed to fetch agent with ID ${agentId}, response status: ${agentResponse.status}`);
            // Use a default type since we can't determine the actual type
            agentTypeId = 'page-role';
            debugLog(`Using default agent type: ${agentTypeId}`);
          }
        } catch (error) {
          debugLog(`Error converting agent ID: ${error}`);
          // Continue with original ID if conversion fails
        }
      }
      
      // Use the API endpoint to save the prompt - matches the endpoint patterns from our tests
      const response = await authFetch(`${API_BASE_URL}/agents/${agentTypeId}/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_type: agentTypeId,
          prompt,
          version: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        debugLog(`Successfully saved prompt for ${agentId}`);
        return result;
      }

      // If the API fails, throw an error instead of returning mock data
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Failed to save prompt: ${response.status} ${errorText}`);
    } catch (error) {
      console.error('Error saving prompt:', error);
      // Re-throw the error instead of returning mock data
      throw error;
    }
  },

  /**
   * Get all prompt versions for a specific agent type
   */
  async getPromptHistory(agentType: string): Promise<AgentPrompt[]> {
    debugLog(`Getting prompt history for ${agentType} (mock implementation)`);
    
    // Mock implementation
    return [
      {
        _id: 'mock-id-1',
        agent_type: agentType,
        prompt: 'Previous version of the prompt...',
        version: '1.0.0',
        created_at: new Date(Date.now() - 86400000) // yesterday
      },
      {
        _id: 'mock-id-2',
        agent_type: agentType,
        prompt: 'Current version of the prompt...',
        version: '1.1.0',
        created_at: new Date()
      }
    ];
  },

  /**
   * Get logs for an agent with optional filters
   */
  /**
   * Get logs for an agent with optional filters
   * OPTIMIZED VERSION - selects best endpoint without trying multiple in sequence
   */
  async getLogs(params: {
    agentType?: string;
    traceId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: AgentLog[]; total: number }> {
    // We need an agent type to fetch logs
    if (!params.agentType) {
      debugLog('No agent type provided, using page-role as default');
      params.agentType = 'page-role';
    }

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.traceId) queryParams.append('trace_id', params.traceId);
      queryParams.append('agent_type', params.agentType);

      // Use a single proven endpoint instead of trying multiple in sequence
      // This significantly improves performance by avoiding cascading API calls
      const url = `${API_BASE_URL}/logs/by-agent-type/${params.agentType}`;
      debugLog(`Fetching logs from optimized URL: ${url}`);
      
      const response = await authFetch(url);
      
      if (!response.ok) {
        // If this endpoint fails, use the query parameter version as fallback
        // This is still faster than trying multiple endpoints in sequence
        const fallbackUrl = `${API_BASE_URL}/agents/logs?${queryParams.toString()}`;
        debugLog(`Primary endpoint failed, using fallback: ${fallbackUrl}`);
        
        const fallbackResponse = await authFetch(fallbackUrl);
        
        if (!fallbackResponse.ok) {
          // If both endpoints fail, return empty logs
          debugLog(`Fallback endpoint also failed with status: ${fallbackResponse.status}`);
          return { logs: [], total: 0 };
        }
        
        // Parse the fallback response
        try {
          const data = await fallbackResponse.json();
          debugLog('Successfully fetched logs from fallback endpoint');
          return data;
        } catch (jsonError) {
          debugLog(`Error parsing JSON from fallback endpoint: ${jsonError.message}`);
          return { logs: [], total: 0 };
        }
      }
      
      // Parse the primary response
      try {
        const data = await response.json();
        debugLog('Successfully fetched logs from primary endpoint');
        return data;
      } catch (jsonError) {
        debugLog(`Error parsing JSON from primary endpoint: ${jsonError.message}`);
        return { logs: [], total: 0 };
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      return { logs: [], total: 0 };
    }
  },

  /**
   * Replay a specific agent log with the current or a specific prompt
   */
  async replayLog(
    logId: string, 
    options: { useCurrentPrompt?: boolean; customPrompt?: string } = {}
  ): Promise<AgentLog> {
    debugLog(`Replaying log ${logId}`);
    
    try {
      // Try to replay the log via API
      const requestBody: any = { logId };
      
      if (options.useCurrentPrompt) {
        requestBody.useCurrentPrompt = true;
      }
      
      if (options.customPrompt) {
        requestBody.customPrompt = options.customPrompt;
      }
      
      // Try to replay the log through the API
      const response = await authFetch(`${API_BASE_URL}/agents/logs/replay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      if (response.ok) {
        const replayResult = await response.json();
        debugLog('Successfully replayed log via API');
        return replayResult;
      }
      
      debugLog(`Failed to replay log ${logId} via API, using mock implementation`);
    } catch (error) {
      console.error(`Error replaying log ${logId}:`, error);
      debugLog('Error replaying log, using mock implementation');
    }
    
    // Fall back to mock implementation
    return {
      _id: 'replay-' + Date.now(),
      traceId: 'trace-789',
      agent_type: 'page-role',
      input: { pageId: 'page-3', sessionId: 'session-3', pageContent: '<html>...</html>' },
      prompt: options.customPrompt || 'Current prompt...',
      raw_output: { roles: [{ role: 'product_detail', confidence: 0.9, evidence: 'Contains single product' }] },
      parsed_output: { roles: [{ role: 'product_detail', confidence: 0.9, evidence: 'Contains single product' }] },
      timestamp: new Date()
    };
  },

  /**
   * Get a specific log by ID
   */
  async getLogById(logId: string): Promise<AgentLog | null> {
    debugLog(`Getting log by ID ${logId}`);
    
    try {
      // Try our specialized trace endpoint first - this avoids MongoDB ObjectId validation issues
      try {
        // The new logs/by-trace endpoint is specifically designed for this purpose
        const traceUrl = `${API_BASE_URL}/logs/by-trace/${logId}`;
        debugLog(`Trying specialized by-trace endpoint: ${traceUrl}`);
        
        const traceResponse = await authFetch(traceUrl);
        if (traceResponse.ok) {
          const contentType = traceResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const traceData = await traceResponse.json();
            if (traceData.log) {
              debugLog('Successfully fetched log from by-trace endpoint');
              return traceData.log;
            }
          } else {
            debugLog(`Trace endpoint returned non-JSON content type: ${contentType}`);
          }
        } else {
          debugLog(`Trace endpoint failed with status: ${traceResponse.status}`);
        }
      } catch (traceError) {
        debugLog(`Trace endpoint error: ${traceError.message}`);
      }
      
      // Try the debug endpoint as backup
      try {
        const debugUrl = `${API_BASE_URL}/debug/logs?trace_id=${logId}`;
        debugLog(`Trying debug logs endpoint with trace_id: ${debugUrl}`);
        
        const debugResponse = await authFetch(debugUrl);
        if (debugResponse.ok) {
          const contentType = debugResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const debugData = await debugResponse.json();
            if (debugData.logs && debugData.logs.length > 0) {
              debugLog('Found log via debug endpoint with trace_id match');
              const log = debugData.logs[0];
              return log;
            }
          } else {
            debugLog(`Debug endpoint returned non-JSON content type: ${contentType}`);
          }
        } else {
          debugLog(`Debug endpoint failed with status: ${debugResponse.status}`);
        }
      } catch (debugError) {
        debugLog(`Debug endpoint error: ${debugError.message}`);
      }
      
      // Standard logs endpoint (tries both ObjectId and trace_id lookup)
      debugLog(`Trying standard logs endpoint for ${logId}`);
      try {
        const response = await authFetch(`${API_BASE_URL}/logs/${logId}`);
        
        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            try {
              const data = await response.json();
              debugLog('Successfully retrieved log from standard logs endpoint');
              return data.log;
            } catch (jsonError) {
              debugLog(`Error parsing JSON from standard endpoint: ${jsonError.message}`);
            }
          }
        } else {
          debugLog(`Standard endpoint failed with status: ${response.status}`);
        }
      } catch (standardError) {
        debugLog(`Standard logs endpoint error: ${standardError.message}`);
      }
      
      // Try older endpoints as fallbacks
      // Agents endpoint (older pattern)
      debugLog(`Trying agents/logs endpoint for ${logId}`);
      try {
        const agentsResponse = await authFetch(`${API_BASE_URL}/agents/logs?trace_id=${logId}`);
        
        if (agentsResponse.ok) {
          const contentType = agentsResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            try {
              const agentsData = await agentsResponse.json();
              if (agentsData.logs && agentsData.logs.length > 0) {
                debugLog('Found log via agents/logs with trace_id parameter');
                return agentsData.logs[0];
              }
            } catch (jsonError) {
              debugLog(`Error parsing JSON from agents endpoint: ${jsonError.message}`);
            }
          }
        } else {
          debugLog(`Agents endpoint failed with status: ${agentsResponse.status}`);
        }
      } catch (agentsError) {
        debugLog(`Agents logs endpoint error: ${agentsError.message}`);
      }
      
      // After all attempts failed, first try the safe bypass endpoint
      try {
        // This is guaranteed to work as it doesn't access the database and won't throw errors
        const bypassUrl = `${API_BASE_URL}/bypass/log/${logId}`;
        debugLog(`Trying fail-safe bypass endpoint: ${bypassUrl}`);
        
        const bypassResponse = await authFetch(bypassUrl);
        if (bypassResponse.ok) {
          const bypassData = await bypassResponse.json();
          if (bypassData && bypassData.log) {
            debugLog('Got mock data from bypass endpoint');
            return { 
              ...bypassData.log,
              isMock: true,
              note: 'This is mock data from the bypass endpoint'
            };
          }
        }
      } catch (bypassError) {
        debugLog(`Bypass endpoint error: ${bypassError.message}`);
      }
      
      debugLog(`Failed to retrieve log with ID ${logId} from all endpoints`);
      
      // Return null to indicate log not found
      return null;
    } catch (error) {
      console.error(`Error fetching log ${logId}:`, error);
      return null;
    }
  },

  /**
   * Run a test agent execution
   */
  async testAgentExecution(input: AgentTestInput): Promise<AgentTestResult> {
    debugLog(`Testing agent execution for ${input.agent_type}`, input);

    try {
      // Record request start time
      const requestStartTime = Date.now();

      // Ensure all required parameters are included based on our test findings
      // Use different prompt templates based on agent type
      let defaultPrompt;
      if (input.agent_type === 'customer-intent') {
        defaultPrompt = 'Analyze the following customer message to determine the intent.\n\nPage ID: {{pageId}}\nSession ID: {{sessionId}}\nCustomer Message: {{customerMessage}}';
      } else {
        defaultPrompt = 'Analyze the following page content and determine its role(s).\n\nPage ID: {{pageId}}\nSession ID: {{sessionId}}\nContent: {{pageContent}}';
      }
      
      const enhancedInput = {
        ...input,
        // Add prompt if not provided (required by the server)
        prompt: input.prompt || defaultPrompt,
        // Add test_data if not provided (required by the server)
        test_data: input.test_data || {
          pageId: input.pageId,
          sessionId: input.sessionId,
          pageContent: input.agent_type === 'customer-intent' ? '' : input.pageContent,
          customerMessage: input.agent_type === 'customer-intent' ? input.customerMessage || input.pageContent : input.customerMessage
        }
      };

      debugLog('Enhanced input with required parameters', enhancedInput);

      // Make actual API call to the backend - use consistent endpoint from constants
      const response = await authFetch(API_ENDPOINTS.AGENT_TEST, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(enhancedInput),
      });

      if (!response.ok) {
        debugLog(`Test agent execution failed with status: ${response.status}`);
        
        // Try to get more information about the error
        try {
          const errorData = await response.json();
          debugLog(`Error details:`, errorData);
          throw new Error(errorData.message || `Failed to execute agent test: ${response.status}`);
        } catch (parseError) {
          throw new Error(`Failed to execute agent test: ${response.status}`);
        }
      }

      // Calculate total request time (including network latency)
      const totalRequestTime = Date.now() - requestStartTime;

      const result = await response.json();

      // Add timing information to the log
      if (result.log) {
        result.log.total_request_time = totalRequestTime;
      }

      debugLog('Real API response for agent execution:', result);
      return result;
    } catch (error) {
      console.error('Error testing agent:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : String(error),
        log: null as any
      };
    }
  },

  /**
   * Submit a rating for an agent execution
   */
  async rateAgentExecution(rating: AgentRating): Promise<{ success: boolean; rating?: AgentRating; message?: string }> {
    debugLog(`Submitting rating for trace ${rating.traceId}`, rating);

    try {
      // In a real implementation, this would call your backend API
      // For now, we'll mock the response

      // Mock a short delay
      await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 200));

      // Mock rating with ID and timestamp
      const savedRating: AgentRating = {
        ...rating,
        _id: `rating-${Date.now()}`,
        created_at: new Date()
      };

      return {
        success: true,
        rating: savedRating
      };
    } catch (error) {
      console.error('Error submitting rating:', error);
      return {
        success: false,
        message: error.message
      };
    }
  },

  /**
   * Get ratings for a specific trace ID
   */
  async getRatingsByTraceId(traceId: string): Promise<AgentRating[]> {
    debugLog(`Getting ratings for trace ${traceId} (mock implementation)`);

    // Mock a short delay
    await new Promise(resolve => setTimeout(resolve, 200));

    // For mock purposes, sometimes return an existing rating, sometimes empty
    if (Math.random() > 0.7) {
      return [{
        _id: `rating-mock-${Date.now()}`,
        traceId,
        agent_type: traceId.includes('role') ? 'page-role' : traceId.includes('intent') ? 'page-semantic-intent' : 'customer-intent',
        rating: Math.random() > 0.3 ? 'up' : 'down',
        reason: Math.random() > 0.3 ? undefined : 'Could improve accuracy in detecting the primary intent.',
        created_at: new Date(Date.now() - Math.floor(Math.random() * 86400000)) // Random time in the last 24 hours
      }];
    }

    return [];
  }
};